import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Shield, Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ phone: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone || !form.password) return toast.error('Please fill all fields');
    
    try {
      setLoading(true);
      await login(form);
      toast.success('Welcome back, Super Admin!');
      navigate('/dashboard');
    } catch (err) {
      if (err.message === 'Unauthorized role') {
        localStorage.clear();
        toast.error('Access Denied. Please use the mobile app.');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left Side — Branding Panel (Hidden on Mobile) */}
      <div className="hidden lg:flex w-1/2 bg-[#1A2535] relative flex-col justify-center items-center text-center p-12 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[20%] left-[20%] w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-[20%] right-[20%] w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="mx-auto w-20 h-20 bg-teal-500/20 flex items-center justify-center rounded-2xl mb-8">
            <Shield size={40} className="text-teal-400" />
          </div>
          <h1 className="text-4xl font-extrabold text-white tracking-tight">Digital Bhishi</h1>
          <p className="text-lg text-gray-400 font-medium">
            Super Admin Control Panel
          </p>
          <div className="pt-8">
            <p className="text-sm text-gray-500 leading-relaxed">
              Securely manage platform owners, oversee financial groups, and monitor system health from a unified administrative command center.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side — Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 xl:p-24 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-50 rounded-2xl mb-4">
              <Shield size={32} className="text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Digital Bhishi</h2>
            <p className="text-sm text-gray-500 mt-1">Super Admin Panel</p>
          </div>

          <div className="text-left">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-500">Please enter your super admin credentials.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Enter 10-digit mobile number"
                autoComplete="tel username"
                className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 rounded-xl px-4 py-3.5 pr-12 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShow(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600 transition-colors p-1"
                >
                  {show ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl py-3.5 mt-4 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <LoadingSpinner size="sm" color="text-white" /> : <LogIn size={20} />}
              {loading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>

          <div className="pt-8 text-center border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400">
              <span className="text-red-500 mr-1">⚠️</span>
              Strictly restricted to authorized Super Administrators
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
